import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import Button from '../../Button';
import ClassBasicInfo from './BasicInfo';
import ClassLessonInfo2 from './LessonInfo2';
import ClassEditor2 from './Editor2';
import ConfirmModal from '../../modals/ConfirmModal';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import Loader from '../../Loader';
import ErrorResponse from '../../ErrorResponse';
import {
  closeClass,
  createClass2,
  graduateClass,
  updateClass2,
  updateClassInfo2,
  updateClassSubject,
} from '../../../api/campusClasses.api';
import useNotification from '../../../hooks/useNotification';

interface IProps {
  tab: string;
  setTab: (tab: string, classCode?: string, campusCode?: string, semesterCode?: string) => void;
  doGetClassData: (classCode: string) => void;
  setRefreshTable: any;
}

const ClassInfo: FC<IProps> = ({
  tab,
  setTab,
  doGetClassData,
  setRefreshTable,
}) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { chosenClass } = useSelector((state: RootState) => state.class);
  const { successNotification } = useNotification();
  const {
    data: createRes,
    isLoading: createIsLoading,
    error: createError,
    execute: createExecute,
  } = useAxios<any>();
  const {
    data: updateRes,
    isLoading: updateIsLoading,
    error: updateError,
    execute: updateExecute,
  } = useAxios();
  const {
    data: closeRes,
    isLoading: closeIsLoading,
    error: closeError,
    execute: closeExecute,
  } = useAxios();
  const {data: gradRes, isLoading: gradIsLoading, error: gradError, execute: gradExecute} = useAxios();

  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);
  const [confirmGraduateVisible, setConfirmGraduateVisible] = useState(false);

  useEffect(() => {
    if (createRes) {
      setTab('info', createRes.data?.classCode, createRes.data?.campusCode, createRes.data?.semesterCode);
      setRefreshTable((current: boolean) => !current);
      successNotification(t('common.message.save1'));
    }
  }, [createRes]);

  useEffect(() => {
    if (updateRes) {
      if (chosenClass) doGetClassData(chosenClass.classCode);
      setTab('info');
      setRefreshTable((current: boolean) => !current);
      successNotification(t('common.message.save1'));
    }
  }, [updateRes]);

  useEffect(() => {
    if (closeRes) {
      if (chosenClass) doGetClassData(chosenClass.classCode);
      successNotification(t('class.class.message1'));
    }
  }, [closeRes]);

  useEffect(() => {
    if (gradRes) {
      if (chosenClass) doGetClassData(chosenClass.classCode);
      successNotification(t('common.button.processed'));
    }
  }, [gradRes]);

  const createClass = async (campusCode: string, req: any) => {
    createExecute(createClass2(campusCode, req));
  };

  const updateClass = async (type: number, classCode: string, req: any) => {
    if (type === 1) {
      updateExecute(updateClass2(classCode, req));
    } else if (type === 2) {
      updateExecute(updateClassInfo2(classCode, req));
    } else {
      updateExecute(updateClassSubject(classCode, req));
    }
  };

  const onCloseClass = () => {
    if (!chosenClass) return;
    closeExecute(closeClass({ classCode: chosenClass.classCode }));
  };

  const onGraduateClass = () => {
    if (!chosenClass) return;
    gradExecute(graduateClass({ classCode: chosenClass.classCode }));
  }

  const renderTabContent = () => {
    switch (tab) {
      case 'info':
        return (
          <>
            <ClassBasicInfo data={chosenClass} />
            {/*@ts-ignore*/}
            <ClassLessonInfo2 data={chosenClass} />
            <div className="comm-bx__space">
              <div className="comm-btn__area right">
                {(chosenClass?.semesterType === 'SC') &&
                  <Button 
                    className="btn-basic pink-line" 
                    text={t('class.class.button17')}
                    onClick={() => setConfirmGraduateVisible(true)}
                    writeAuthYn={writeAuthYn}
                  />
                }
                <Button
                  className="btn-basic gray"
                  onClick={() => setConfirmCloseVisible(true)}
                  text={t('class.class.column9')}
                  writeAuthYn={writeAuthYn}
                />
                <Button
                  className="btn-basic pink-line"
                  onClick={() => setTab('updateClass')}
                  text={t('common.button.edit')}
                  writeAuthYn={writeAuthYn}
                />
              </div>
            </div>
          </>
        );
      case 'createClass':
        return (
          <ClassEditor2
            submit={createClass}
          />
        );
      case 'updateClass':
        return (
          <ClassEditor2
            classData={chosenClass}
            submit={updateClass}
          />
        );
    }
  };

  return (
    <>
      {(createIsLoading || updateIsLoading || closeIsLoading) && <Loader />}
      <ErrorResponse error={createError} />
      <ErrorResponse error={updateError} />
      <ErrorResponse error={closeError} />
      <ErrorResponse error={gradError} />

      {renderTabContent()}

      {confirmCloseVisible && (
        <ConfirmModal
          visible={confirmCloseVisible}
          setVisible={setConfirmCloseVisible}
          onConfirm={onCloseClass}
          titleText={t('class.class.title4')}
          messageText={t('class.class.text9')}
          confirmText={t('class.class.column9')}
          cancelText={t('common.button.cancel')}
        />
      )}

      {confirmGraduateVisible && (
        <ConfirmModal
          visible={confirmGraduateVisible}
          setVisible={setConfirmGraduateVisible}
          onConfirm={onGraduateClass}
          titleText={t('class.class.button17')}
          messageText={t('class.class.text35')}
          confirmText={t('common.button.confirm')}
          cancelText={t('common.button.cancel')}
        />
      )}
    </>
  );
};

export default ClassInfo;
